import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Camera, Image as ImageIcon, Trash2, Check, Sparkles } from "lucide-react";
import { ART } from "../assets";
import { track } from "../lib/track";
import { PickupPicker, defaultPickupDate, WINDOWS } from "./PickupPicker";
import { formatPhone } from "../lib/format";

const PROCESS_STEPS = [
  "Detecting items",
  "Calculating volume",
  "Checking donation options",
  "Estimating cost",
  "Estimating labor",
  "Finding recycling",
  "Generating your estimate",
];

const URGENCY = ["This week", "Next few days", "As soon as possible"];

const TOGGLES = [
  { key: "inside", label: "Inside the home" },
  { key: "outside", label: "Outside / curbside" },
  { key: "stairs", label: "Stairs / upper floor" },
  { key: "no_elevator", label: "No elevator" },
  { key: "long_carry", label: "Long carry to truck" },
  { key: "heavy", label: "Very heavy items" },
  { key: "disassembly", label: "Needs disassembly" },
  { key: "parking", label: "Easy parking" },
];

const DETECTED = ["Sofa", "Boxes", "Mattress", "Dining table"];
const INCLUDES = ["Loading", "Transportation", "Responsible disposal", "Cleanup"];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", address: "", city: "", zip: "", notes: "" };

async function fileToBase64Resized(file, maxDim = 1024) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      if (width >= height && width > maxDim) {
        height = Math.round((height * maxDim) / width);
        width = maxDim;
      } else if (height > maxDim) {
        width = Math.round((width * maxDim) / height);
        height = maxDim;
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.82).split(",")[1]);
    };
    img.onerror = reject;
    img.src = url;
  });
}

export default function EstimateFlow({ open, onClose }) {
  const [stage, setStage] = useState("upload"); // upload | processing | result | booked
  const [images, setImages] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [form, setForm] = useState(empty);
  const [urgency, setUrgency] = useState(URGENCY[0]);
  const [toggles, setToggles] = useState({ inside: true });
  const [procIndex, setProcIndex] = useState(0);
  const [estimate, setEstimate] = useState(null);
  const [leadId, setLeadId] = useState(null);
  const [estimateError, setEstimateError] = useState(false);
  const [pickupDate, setPickupDate] = useState(defaultPickupDate());
  const [pickupWindow, setPickupWindow] = useState(WINDOWS[0]);
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [editingItems, setEditingItems] = useState(false);
  const [itemsDirty, setItemsDirty] = useState(false);
  const [addQuery, setAddQuery] = useState("");
  const [repricing, setRepricing] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const reset = useCallback(() => {
    setStage("upload");
    setImages((imgs) => {
      imgs.forEach((i) => URL.revokeObjectURL(i.url));
      return [];
    });
    setForm(empty);
    setUrgency(URGENCY[0]);
    setToggles({ inside: true });
    setProcIndex(0);
    setEstimate(null);
    setLeadId(null);
    setEstimateError(false);
    setPickupDate(defaultPickupDate());
    setPickupWindow(WINDOWS[0]);
    setItems([]);
    setEditingItems(false);
    setItemsDirty(false);
    setAddQuery("");
    setRepricing(false);
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      track("quote_started");
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // processing animation (visual only — completion is driven by the API call)
  useEffect(() => {
    if (stage !== "processing") return;
    setProcIndex(0);
    const interval = setInterval(() => {
      setProcIndex((i) => (i >= PROCESS_STEPS.length - 1 ? i : i + 1));
    }, 480);
    return () => clearInterval(interval);
  }, [stage]);

  const addFiles = (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    setImages((prev) => {
      const room = 8 - prev.length;
      const next = files.slice(0, room).map((f) => ({
        id: `${f.name}-${f.size}-${Math.random()}`,
        url: URL.createObjectURL(f),
        file: f,
      }));
      if (prev.length === 0 && next.length > 0) track("photos_uploaded");
      return [...prev, ...next];
    });
  };

  const removeImage = (id) =>
    setImages((prev) => {
      const found = prev.find((i) => i.id === id);
      if (found) URL.revokeObjectURL(found.url);
      return prev.filter((i) => i.id !== id);
    });

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const toggle = (key) => setToggles((t) => ({ ...t, [key]: !t[key] }));
  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleGetEstimate = async () => {
    setStage("processing");
    setEstimateError(false);
    try {
      const b64s = await Promise.all(images.map((im) => fileToBase64Resized(im.file)));
      const fetchPromise = fetch(`${API}/estimate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          urgency,
          inside: !!toggles.inside,
          outside: !!toggles.outside,
          stairs: !!toggles.stairs,
          heavy: !!toggles.heavy,
          parking: !!toggles.parking,
          no_elevator: !!toggles.no_elevator,
          long_carry: !!toggles.long_carry,
          disassembly: !!toggles.disassembly,
          images: b64s,
          lead_id: leadId,
        }),
      }).then((r) => (r.ok ? r.json() : null));
      // keep the processing animation on screen for a beat even if the API is fast
      const [data] = await Promise.all([
        fetchPromise,
        new Promise((r) => setTimeout(r, 3600)),
      ]);
      if (data && data.estimate) {
        setEstimate(data.estimate);
        setItems(data.estimate.items || []);
        setEditingItems(false);
        setItemsDirty(false);
        setLeadId(data.lead_id);
        track("estimate_viewed");
      } else {
        setEstimateError(true);
      }
    } catch (e) {
      console.error("Estimate failed", e);
      setEstimateError(true);
    } finally {
      setStage("result");
    }
  };

  const openItemEditor = async () => {
    setEditingItems(true);
    if (catalog.length === 0) {
      try {
        const r = await fetch(`${API}/pricing/catalog`);
        const d = await r.json();
        setCatalog(d.items || []);
      } catch (e) {
        console.error("Catalog load failed", e);
      }
    }
  };

  const changeItemCount = (key, delta) => {
    setItemsDirty(true);
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, count: Math.max(1, i.count + delta) } : i)));
  };

  const removeItem = (key) => {
    setItemsDirty(true);
    setItems((prev) => prev.filter((i) => i.key !== key));
  };

  const addItem = (c) => {
    setItemsDirty(true);
    setAddQuery("");
    setItems((prev) => {
      const existing = prev.find((i) => i.key === c.key);
      return existing
        ? prev.map((i) => (i.key === c.key ? { ...i, count: i.count + 1 } : i))
        : [...prev, { key: c.key, label: c.label, count: 1 }];
    });
  };

  const repriceQuote = async () => {
    if (items.length === 0) return;
    setRepricing(true);
    try {
      const res = await fetch(`${API}/estimate/reprice`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lead_id: leadId,
          items: items.map((i) => ({ key: i.key, count: i.count })),
          stairs: !!toggles.stairs,
          heavy: !!toggles.heavy,
          parking: !!toggles.parking,
          inside: !!toggles.inside,
          outside: !!toggles.outside,
          no_elevator: !!toggles.no_elevator,
          long_carry: !!toggles.long_carry,
          disassembly: !!toggles.disassembly,
        }),
      });
      const data = res.ok ? await res.json() : null;
      if (data && data.estimate) {
        setEstimate(data.estimate);
        setItems(data.estimate.items || []);
        setItemsDirty(false);
        setEditingItems(false);
        track("items_edited");
      }
    } catch (e) {
      console.error("Reprice failed", e);
    } finally {
      setRepricing(false);
    }
  };

  const [submitting, setSubmitting] = useState(false);
  const [payMethod, setPayMethod] = useState("cash");
  const [tipPct, setTipPct] = useState(0);

  const submitLead = async () => {
    setSubmitting(true);
    try {
      if (payMethod === "card" && leadId) {
        const res = await fetch(`${API}/payments/checkout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            origin_url: window.location.origin,
            lead_id: leadId,
            pickup_date: pickupDate,
            pickup_window: pickupWindow,
            tip_pct: tipPct,
          }),
        });
        const data = await res.json();
        if (data.url) {
          track("booked");
          window.location.href = data.url;
          return;
        }
        throw new Error("checkout failed");
      }
      if (leadId) {
        await fetch(`${API}/leads/${leadId}/book`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pickup_date: pickupDate, pickup_window: pickupWindow, payment_method: "cash", base_url: window.location.origin }),
        });
      }
      track("booked");
      setStage("booked");
    } catch (e) {
      console.error("Booking failed", e);
      setStage("booked");
    } finally {
      setSubmitting(false);
    }
  };

  const result = estimate || {
    price: "$329",
    confidence: "94%",
    detected_items: DETECTED,
    space_reclaimed: "72 sq ft",
    time_saved: "4 hours",
    quote_type: "quote",
    manual_review: false,
    disclaimer: "Final price confirmed on-site.",
  };

  const close = () => {
    onClose();
    setTimeout(reset, 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-stretch sm:items-center sm:justify-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="estimate-flow"
        >
          <div className="absolute inset-0 bg-charcoal/40 backdrop-blur-sm" onClick={close} />

          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 flex w-full max-w-2xl flex-col overflow-hidden bg-canvas sm:rounded-[2.5rem] shadow-[0_40px_120px_-30px_rgba(23,23,23,0.6)] max-h-screen sm:max-h-[92vh]"
          >
            {/* header */}
            <div className="flex items-center justify-between px-6 py-5 sm:px-8 border-b border-stone/60">
              <span className="font-logo font-semibold text-2xl lowercase text-violet">
                milla
              </span>
              <button
                onClick={close}
                data-testid="estimate-close"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white/70 text-charcoal/70 hover:bg-stone/60 transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-7 sm:px-8 sm:py-9">
              <AnimatePresence mode="wait">
                {/* ---------------- UPLOAD ---------------- */}
                {stage === "upload" && (
                  <motion.div
                    key="upload"
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -14 }}
                    transition={{ duration: 0.4 }}
                  >
                    <h2 className="font-display text-3xl tracking-tight text-charcoal sm:text-4xl">
                      Show us the clutter.
                    </h2>
                    <p className="mt-3 text-charcoal/60">
                      Add a few photos and a couple of details. We'll do the thinking.
                    </p>

                    {/* dropzone */}
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragging(true);
                      }}
                      onDragLeave={() => setDragging(false)}
                      onDrop={onDrop}
                      data-testid="dropzone"
                      className={`mt-7 rounded-[1.8rem] border-2 border-dashed p-7 text-center transition-colors duration-300 ${
                        dragging ? "border-violet bg-lavender/40" : "border-stone bg-white/40"
                      }`}
                    >
                      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-lavender text-violet">
                        <ImageIcon size={24} />
                      </span>
                      <p className="mt-4 text-charcoal font-medium">
                        Drag & drop photos here
                      </p>
                      <p className="text-sm text-charcoal/50">or choose from your device · up to 8</p>

                      <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                        <button
                          onClick={() => fileRef.current?.click()}
                          data-testid="gallery-upload-btn"
                          className="inline-flex items-center gap-2 rounded-full bg-charcoal px-5 py-2.5 text-sm font-semibold text-canvas hover:bg-charcoal/85 transition-colors"
                        >
                          <ImageIcon size={16} /> Gallery
                        </button>
                        <button
                          onClick={() => cameraRef.current?.click()}
                          data-testid="camera-upload-btn"
                          className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-charcoal border border-stone hover:border-violet hover:text-violet transition-colors"
                        >
                          <Camera size={16} /> Camera
                        </button>
                      </div>

                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        multiple
                        hidden
                        data-testid="file-input"
                        onChange={(e) => e.target.files && addFiles(e.target.files)}
                      />
                      <input
                        ref={cameraRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        hidden
                        data-testid="camera-input"
                        onChange={(e) => e.target.files && addFiles(e.target.files)}
                      />
                    </div>

                    {/* previews */}
                    {images.length > 0 && (
                      <div className="mt-5">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-charcoal/70">
                            {images.length} of 8 photos
                          </span>
                        </div>
                        <div className="mt-3 grid grid-cols-4 gap-3" data-testid="image-grid">
                          {images.map((img) => (
                            <div key={img.id} className="group relative aspect-square overflow-hidden rounded-2xl bg-stone/40">
                              <img src={img.url} alt="upload" className="h-full w-full object-cover" />
                              <button
                                onClick={() => removeImage(img.id)}
                                data-testid={`remove-image-${img.id}`}
                                className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-charcoal/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                aria-label="Remove"
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* details form */}
                    <div className="mt-9 space-y-5">
                      <div className="grid gap-4 sm:grid-cols-2">
                        <Field label="Name" testid="field-name" value={form.name} onChange={update("name")} placeholder="Your name" />
                        <Field label="Email" testid="field-email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" />
                        <Field label="Phone" testid="field-phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))} placeholder="555-000-0000" />
                        <Field label="Address" testid="field-address" value={form.address} onChange={update("address")} placeholder="Street address" />
                        <Field label="City" testid="field-city" required value={form.city} onChange={update("city")} placeholder="City" />
                        <Field label="ZIP code" testid="field-zip" required value={form.zip} onChange={update("zip")} placeholder="00000" />
                      </div>
                      <Field label="Notes" testid="field-notes" value={form.notes} onChange={update("notes")} placeholder="Anything we should know?" textarea />
                    </div>

                    {/* urgency */}
                    <div className="mt-7">
                      <span className="text-sm font-medium text-charcoal/70">When works for you?</span>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {URGENCY.map((u) => (
                          <button
                            key={u}
                            onClick={() => setUrgency(u)}
                            data-testid={`urgency-${u.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`rounded-full px-4 py-2 text-sm font-medium transition-all duration-300 ${
                              urgency === u
                                ? "bg-violet text-white"
                                : "bg-white text-charcoal/70 border border-stone hover:border-violet"
                            }`}
                          >
                            {u}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* toggles */}
                    <div className="mt-7">
                      <span className="text-sm font-medium text-charcoal/70">A few details</span>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {TOGGLES.map((t) => (
                          <button
                            key={t.key}
                            onClick={() => toggle(t.key)}
                            data-testid={`toggle-${t.key}`}
                            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-300 ${
                              toggles[t.key]
                                ? "bg-sage/50 text-charcoal border border-sage"
                                : "bg-white text-charcoal/60 border border-stone hover:border-sage"
                            }`}
                          >
                            <span className={`flex h-4 w-4 items-center justify-center rounded-full ${toggles[t.key] ? "bg-violet text-white" : "bg-stone/60"}`}>
                              {toggles[t.key] && <Check size={11} />}
                            </span>
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <button
                      disabled={images.length === 0 || !form.city.trim() || !form.zip.trim()}
                      onClick={handleGetEstimate}
                      data-testid="get-estimate-btn"
                      className="mt-9 inline-flex w-full items-center justify-center gap-2 rounded-full bg-violet px-8 py-4 text-base font-semibold text-white transition-all duration-300 enabled:hover:bg-violet-deep enabled:hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Sparkles size={18} /> Get my estimate
                    </button>
                    {(images.length === 0 || !form.city.trim() || !form.zip.trim()) && (
                      <p className="mt-3 text-center text-xs text-charcoal/45" data-testid="estimate-gate-hint">
                        {images.length === 0
                          ? "Add at least one photo to continue"
                          : "City and ZIP code are required to continue"}
                      </p>
                    )}
                  </motion.div>
                )}

                {/* ---------------- PROCESSING ---------------- */}
                {stage === "processing" && (
                  <motion.div
                    key="processing"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="py-6 text-center"
                  >
                    <div className="relative mx-auto h-28 w-28">
                      <span className="absolute inset-0 rounded-full border-2 border-stone/60" />
                      <motion.span
                        className="absolute inset-0 rounded-full border-2 border-violet border-t-transparent"
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1.2, ease: "linear" }}
                      />
                      <span className="absolute inset-0 flex items-center justify-center text-violet">
                        <Sparkles size={30} />
                      </span>
                    </div>
                    <h2 className="mt-8 font-display text-3xl tracking-tight text-charcoal">
                      A little quiet magic.
                    </h2>
                    <p className="mt-2 text-charcoal/55">Reading your space, one detail at a time.</p>

                    <div className="mx-auto mt-8 max-w-sm space-y-1">
                      {PROCESS_STEPS.map((label, i) => {
                        const done = i < procIndex;
                        const active = i === procIndex;
                        return (
                          <div
                            key={label}
                            data-testid={`process-step-${i}`}
                            className={`flex items-center gap-3 rounded-2xl px-4 py-3 text-left transition-all duration-500 ${
                              active ? "bg-lavender/60" : "bg-transparent"
                            }`}
                          >
                            <span
                              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors duration-300 ${
                                done ? "bg-sage text-white" : active ? "bg-violet text-white" : "bg-stone/50 text-transparent"
                              }`}
                            >
                              {done ? <Check size={13} /> : active ? (
                                <motion.span
                                  className="h-1.5 w-1.5 rounded-full bg-white"
                                  animate={{ scale: [1, 1.6, 1] }}
                                  transition={{ repeat: Infinity, duration: 0.9 }}
                                />
                              ) : null}
                            </span>
                            <span
                              className={`text-sm transition-colors duration-300 ${
                                done ? "text-charcoal/45" : active ? "text-charcoal font-semibold" : "text-charcoal/40"
                              }`}
                            >
                              {label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                {/* ---------------- RESULT ---------------- */}
                {stage === "result" && (
                  <motion.div
                    key="result"
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -14 }}
                    data-testid="estimate-result"
                  >
                    {estimateError ? (
                      <div className="py-10 text-center" data-testid="estimate-error">
                        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-stone/60 text-charcoal">
                          <X size={26} />
                        </span>
                        <h2 className="mt-6 font-display text-3xl tracking-tight text-charcoal">
                          That didn't go through.
                        </h2>
                        <p className="mx-auto mt-3 max-w-sm text-charcoal/60">
                          We couldn't read your photos just now. Mind trying again with a clear, well-lit shot?
                        </p>
                        <button
                          onClick={() => setStage("upload")}
                          data-testid="estimate-retry-btn"
                          className="mt-7 inline-flex items-center justify-center rounded-full bg-violet px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-violet-deep hover:-translate-y-0.5"
                        >
                          Try again
                        </button>
                      </div>
                    ) : (
                    <>
                    <span className="inline-flex items-center gap-2 rounded-full bg-sage/40 px-3 py-1.5 text-xs font-semibold text-charcoal">
                      <Check size={13} /> Estimate ready
                    </span>
                    <h2 className="mt-4 font-display text-3xl tracking-tight text-charcoal sm:text-4xl">
                      Here's your space, costed out.
                    </h2>

                    {/* price hero */}
                    <div className="mt-7 rounded-[1.8rem] bg-violet px-7 py-6 text-white">
                      <p className="text-sm text-white/70">
                        {result.manual_review ? "Your pickup" : "Your price"}
                      </p>
                      <p className="font-display text-5xl tracking-tight" data-testid="estimate-price">{result.price}</p>
                    </div>

                    {result.manual_review && (
                      <div className="mt-4 rounded-2xl bg-lavender/50 px-5 py-4 text-sm text-charcoal" data-testid="review-note">
                        A milla specialist will confirm your exact price — {result.review_reason || "this one needs a quick human look"}. Book below and we'll reach out fast.
                      </div>
                    )}

                    {/* detected + item editor */}
                    <div className="mt-6" data-testid="items-section">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium text-charcoal/70">We spotted</p>
                        {!editingItems && items.length > 0 && !result.review_locked && (
                          <button
                            onClick={openItemEditor}
                            data-testid="edit-items-btn"
                            className="text-sm font-semibold text-violet hover:text-violet-deep transition-colors"
                          >
                            Not quite right? Edit items
                          </button>
                        )}
                      </div>
                      {!editingItems ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {(items.length > 0
                            ? items.map((i) => (i.count > 1 ? `${i.label} ×${i.count}` : i.label))
                            : result.detected_items
                          ).map((d) => (
                            <span key={d} className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-2 text-sm font-medium text-charcoal border border-stone">
                              <Check size={13} className="text-sage" /> {d}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="mt-3 space-y-2" data-testid="item-editor">
                          {items.map((it) => (
                            <div key={it.key} className="flex items-center justify-between rounded-2xl border border-stone bg-white px-4 py-2.5">
                              <span className="text-sm font-medium text-charcoal">{it.label}</span>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => changeItemCount(it.key, -1)}
                                  data-testid={`item-dec-${it.key}`}
                                  className="flex h-7 w-7 items-center justify-center rounded-full bg-stone/50 text-charcoal hover:bg-stone transition-colors"
                                  aria-label="Decrease"
                                >
                                  −
                                </button>
                                <span className="w-6 text-center text-sm font-semibold text-charcoal" data-testid={`item-count-${it.key}`}>
                                  {it.count}
                                </span>
                                <button
                                  onClick={() => changeItemCount(it.key, 1)}
                                  data-testid={`item-inc-${it.key}`}
                                  className="flex h-7 w-7 items-center justify-center rounded-full bg-stone/50 text-charcoal hover:bg-stone transition-colors"
                                  aria-label="Increase"
                                >
                                  +
                                </button>
                                <button
                                  onClick={() => removeItem(it.key)}
                                  data-testid={`item-remove-${it.key}`}
                                  className="ml-1 flex h-7 w-7 items-center justify-center rounded-full text-charcoal/50 hover:bg-stone/50 hover:text-charcoal transition-colors"
                                  aria-label="Remove item"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </div>
                          ))}
                          {items.length === 0 && (
                            <p className="text-sm text-charcoal/50" data-testid="no-items-note">
                              No items yet — add at least one below.
                            </p>
                          )}
                          <div className="relative">
                            <input
                              value={addQuery}
                              onChange={(e) => setAddQuery(e.target.value)}
                              placeholder="Add an item (e.g. sofa, dresser…)"
                              data-testid="add-item-input"
                              className="w-full rounded-2xl border border-stone bg-white px-4 py-2.5 text-sm text-charcoal placeholder:text-charcoal/35 outline-none transition-colors focus:border-violet"
                            />
                            {addQuery.trim() && (
                              <div className="absolute z-20 mt-1 max-h-44 w-full overflow-y-auto rounded-2xl border border-stone bg-white shadow-lg" data-testid="add-item-results">
                                {catalog
                                  .filter((c) => c.label.toLowerCase().includes(addQuery.trim().toLowerCase()))
                                  .slice(0, 8)
                                  .map((c) => (
                                    <button
                                      key={c.key}
                                      onClick={() => addItem(c)}
                                      data-testid={`add-item-${c.key}`}
                                      className="block w-full px-4 py-2.5 text-left text-sm text-charcoal hover:bg-lavender/40 transition-colors"
                                    >
                                      {c.label}
                                    </button>
                                  ))}
                                {catalog.filter((c) => c.label.toLowerCase().includes(addQuery.trim().toLowerCase())).length === 0 && (
                                  <p className="px-4 py-2.5 text-sm text-charcoal/50">No match — try another word</p>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2 pt-1">
                            <button
                              onClick={repriceQuote}
                              disabled={repricing || items.length === 0 || !itemsDirty}
                              data-testid="update-quote-btn"
                              className="inline-flex flex-1 items-center justify-center rounded-full bg-violet px-5 py-2.5 text-sm font-semibold text-white transition-colors enabled:hover:bg-violet-deep disabled:opacity-40"
                            >
                              {repricing ? "Updating…" : "Update my quote"}
                            </button>
                            <button
                              onClick={() => {
                                setEditingItems(false);
                                setAddQuery("");
                                setItems(estimate?.items || []);
                                setItemsDirty(false);
                              }}
                              data-testid="cancel-edit-items-btn"
                              className="rounded-full border border-stone px-5 py-2.5 text-sm font-medium text-charcoal/60 hover:border-violet hover:text-violet transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* stats */}
                    <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
                      <Stat label="Space reclaimed" value={result.space_reclaimed} />
                      <Stat label="Time saved" value={result.time_saved} />
                      <Stat label="Donation" value="Available" accent />
                    </div>

                    {/* includes */}
                    <div className="mt-6 rounded-[1.8rem] bg-white/60 p-6">
                      <p className="text-sm font-medium text-charcoal/70">Every pickup includes</p>
                      <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5">
                        {INCLUDES.map((inc) => (
                          <li key={inc} className="flex items-center gap-2 text-sm text-charcoal">
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-sage/50 text-charcoal">
                              <Check size={12} />
                            </span>
                            {inc}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-7 rounded-[1.8rem] bg-white/60 p-6">
                      <p className="text-sm font-medium text-charcoal/70">Choose a pickup day &amp; window</p>
                      <div className="mt-3">
                        <PickupPicker date={pickupDate} window={pickupWindow} onDate={setPickupDate} onWindow={setPickupWindow} />
                      </div>
                    </div>

                    {/* payment method */}
                    <div className="mt-5 rounded-[1.8rem] bg-white/60 p-6" data-testid="payment-method">
                      <p className="text-sm font-medium text-charcoal/70">How would you like to pay?</p>
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setPayMethod("cash")}
                          data-testid="pay-cash"
                          className={`rounded-2xl border px-4 py-3 text-left transition-all ${payMethod === "cash" ? "border-violet bg-lavender/40" : "border-stone bg-canvas hover:border-violet/40"}`}
                        >
                          <p className="text-sm font-semibold text-charcoal">💵 Cash on pickup</p>
                          <p className="mt-0.5 text-xs text-charcoal/50">Pay the crew on the day.</p>
                        </button>
                        <button
                          onClick={() => result.quote_type === "quote" && setPayMethod("card")}
                          disabled={result.quote_type !== "quote"}
                          data-testid="pay-card"
                          className={`rounded-2xl border px-4 py-3 text-left transition-all disabled:opacity-50 ${payMethod === "card" ? "border-violet bg-lavender/40" : "border-stone bg-canvas hover:border-violet/40"}`}
                        >
                          <p className="text-sm font-semibold text-charcoal">💳 Pay by card</p>
                          <p className="mt-0.5 text-xs text-charcoal/50">{result.quote_type === "quote" ? "Secure checkout, pay now." : "Available once price is confirmed."}</p>
                        </button>
                      </div>
                      {payMethod === "card" && (
                        <div className="mt-4" data-testid="tip-section">
                          <p className="text-sm font-medium text-charcoal/70">Add a tip for the crew? <span className="text-charcoal/40">(optional)</span></p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {[0, 10, 15, 20].map((t) => (
                              <button
                                key={t}
                                onClick={() => setTipPct(t)}
                                data-testid={`tip-${t}`}
                                className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${tipPct === t ? "bg-sage/60 text-charcoal border border-sage" : "bg-canvas text-charcoal/65 border border-stone hover:border-sage"}`}
                              >
                                {t === 0 ? "No tip" : `${t}%`}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={submitLead}
                      disabled={submitting}
                      data-testid="book-pickup-btn"
                      className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-violet px-8 py-4 text-base font-semibold text-white transition-all duration-300 enabled:hover:bg-violet-deep enabled:hover:-translate-y-0.5 disabled:opacity-60"
                    >
                      {submitting ? "Booking…" : payMethod === "card" ? "Continue to payment" : result.manual_review ? "Request pickup" : "Book pickup"}
                    </button>
                    {result.disclaimer && (
                      <p className="mt-3 text-center text-xs text-charcoal/45" data-testid="estimate-disclaimer">{result.disclaimer}</p>
                    )}
                    <button
                      onClick={() => setStage("upload")}
                      data-testid="result-back-btn"
                      className="mt-3 w-full text-center text-sm font-medium text-charcoal/55 hover:text-violet"
                    >
                      ← Edit photos & details
                    </button>
                    </>
                    )}
                  </motion.div>
                )}

                {/* ---------------- BOOKED ---------------- */}
                {stage === "booked" && (
                  <motion.div
                    key="booked"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="py-10 text-center"
                    data-testid="booked-confirmation"
                  >
                    <div className="mx-auto mb-7 max-w-sm overflow-hidden rounded-[1.8rem] bg-white/50">
                      <img src={ART.truckSchedule} alt="Your milla pickup is scheduled" className="w-full" />
                    </div>
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200, damping: 14 }}
                      className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-sage text-white"
                    >
                      <Check size={36} />
                    </motion.span>
                    <h2 className="mt-7 font-display text-3xl tracking-tight text-charcoal sm:text-4xl">
                      Your space is on its way back.
                    </h2>
                    <p className="mx-auto mt-3 max-w-sm text-charcoal/60">
                      We've got your request{form.name ? `, ${form.name.split(" ")[0]}` : ""}. A friendly
                      crew will reach out shortly to confirm your pickup window.
                    </p>
                    <button
                      onClick={close}
                      data-testid="booked-done-btn"
                      className="mt-8 inline-flex items-center justify-center rounded-full bg-charcoal px-8 py-4 text-base font-semibold text-canvas hover:bg-charcoal/85 transition-colors"
                    >
                      Done
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const Field = ({ label, testid, value, onChange, placeholder, type = "text", textarea, required }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium text-charcoal/70">
      {label}{required && <span className="text-violet"> *</span>}
    </span>
    {textarea ? (
      <textarea
        data-testid={testid}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={3}
        className="w-full resize-none rounded-2xl border border-stone bg-white/60 px-4 py-3 text-charcoal placeholder:text-charcoal/35 outline-none transition-colors focus:border-violet"
      />
    ) : (
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-stone bg-white/60 px-4 py-3 text-charcoal placeholder:text-charcoal/35 outline-none transition-colors focus:border-violet"
      />
    )}
  </label>
);

const Stat = ({ label, value, accent }) => (
  <div className={`rounded-[1.4rem] p-5 ${accent ? "bg-lavender/60" : "bg-white/60"}`}>
    <p className="text-xs font-medium uppercase tracking-wide text-charcoal/50">{label}</p>
    <p className={`mt-1 font-display text-2xl tracking-tight ${accent ? "text-violet" : "text-charcoal"}`}>{value}</p>
  </div>
);
